import { useCallback, useEffect, useRef } from 'react';
import { CANVAS_PAD, PAGE_GAP, usePagePreviewScale } from '../shared/usePagePreviewScale';
import PagePreviewPages from '../shared/PagePreviewPages';
import { buildFieldAnchors, scrollToAnchor } from '../shared/previewFieldAnchors';
import UdyogFormPreview, {
    UDYOG_PAGE_COUNT,
    UDYOG_PAGE_HEIGHT,
    UDYOG_PAGE_WIDTH,
} from './UdyogFormPreview';
import { PAGES } from './udyogMetrics';

const DOCUMENT_HEIGHT = UDYOG_PAGE_HEIGHT * UDYOG_PAGE_COUNT + PAGE_GAP * (UDYOG_PAGE_COUNT - 1);

const FIELD_ANCHORS = buildFieldAnchors(PAGES.map((items) => ({
    rows: items
        .filter((item) => item.field)
        .map((item) => ({ value: item.field, y: item.h ? item.y + item.h / 2 : item.y })),
})));

/** The fields that print through a composed line, for following them on focus. */
const FIELD_ALIASES = {
    nameEn: 'nameEnUpper',
    maritalStatus: 'maritalLabel',
    birthDate: 'birthLine',
    phone: 'phoneLine',
    phoneAlt: 'phoneLine',
};

/**
 * The seven pages inside the sticky preview rail, scaled to fit the card so a
 * whole page is on screen at once. A form this long is useless as a preview if
 * you have to hunt for the line you are typing, so the rail follows the field
 * that has focus. Zoomed in past the card, it is dragged around instead.
 */
export default function UdyogPreviewFrame({ form, previewRef, zoom = 1, focusField }) {
    const [scrollRef, scale, page] = usePagePreviewScale(
        UDYOG_PAGE_WIDTH,
        UDYOG_PAGE_HEIGHT,
        zoom,
        UDYOG_PAGE_COUNT,
    );

    const scaleRef = useRef(scale);
    scaleRef.current = scale;
    useEffect(() => {
        if (!focusField) return;
        scrollToAnchor(scrollRef.current, FIELD_ANCHORS[FIELD_ALIASES[focusField] ?? focusField], {
            scale: scaleRef.current,
            pageHeight: UDYOG_PAGE_HEIGHT,
            pageGap: PAGE_GAP,
            pad: CANVAS_PAD,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusField]);

    const assignPreviewRef = useCallback(
        (node) => {
            if (typeof previewRef === 'function') {
                previewRef(node);
            } else if (previewRef) {
                previewRef.current = node;
            }
        },
        [previewRef],
    );

    return (
        // The page marker is pinned to the card, not to the paper, so it stays
        // put while the pages scroll under it.
        <div className="relative flex min-h-0 w-full flex-1">
            <div
                ref={scrollRef}
                className="w-full min-h-0 flex-1 select-none overflow-auto rounded-lg bg-[#3d3d3d]"
                style={{ padding: CANVAS_PAD }}
            >
                {/* The scaled paper is laid out at full size, so the scroll box is
                    given the height the transform actually paints. */}
                <div
                    style={{
                        height: DOCUMENT_HEIGHT * scale,
                        width: UDYOG_PAGE_WIDTH * scale,
                        margin: '0 auto',
                    }}
                >
                    <div
                        className="shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
                        style={{
                            width: UDYOG_PAGE_WIDTH,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                        }}
                    >
                        <UdyogFormPreview ref={assignPreviewRef} form={form} />
                    </div>
                </div>
            </div>
            <PagePreviewPages page={page} total={UDYOG_PAGE_COUNT} />
        </div>
    );
}
